import React from 'react';
import { Activity, TrendingUp, Layers, Zap, BarChart2, Shield, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

interface OnChainProps {
  onNavigate: (page: string) => void;
}

const METRICS = [
  { icon: <TrendingUp size={18} />, label: 'MVRV Ratio',       desc: 'Identify market tops and bottoms by comparing market cap to realized cap.' },
  { icon: <Layers size={18} />,     label: 'LTH Supply %',     desc: 'Track long-term holder conviction — the smart money signal.' },
  { icon: <BarChart2 size={18} />,  label: 'ETF Flows',        desc: 'Daily and 7-day net inflows across all U.S. Bitcoin spot ETFs.' },
  { icon: <Zap size={18} />,        label: 'Funding Rate',     desc: 'Perpetual futures sentiment — longs vs. shorts in real time.' },
  { icon: <Activity size={18} />,   label: 'Hashrate & Diff.', desc: 'Network security, miner health, and difficulty adjustments.' },
  { icon: <Shield size={18} />,     label: 'SOPR',             desc: 'Spent Output Profit Ratio — are sellers in profit or pain?' },
];

const OnChain: React.FC<OnChainProps> = ({ onNavigate }) => {
  useSEO({
    title: 'On-Chain Metrics',
    description: 'Live Bitcoin on-chain metrics including MVRV Ratio, LTH Supply %, ETF Flows, Funding Rate, Hashrate, and SOPR. Data-driven signals for informed decisions.',
    url: 'https://utxomacro.com/on-chain',
    keywords: 'Bitcoin on-chain metrics, MVRV ratio, LTH supply, ETF flows, funding rate, SOPR, hashrate, UTXOMacro',
  });
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A0A0A' }}>
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-24 flex flex-col justify-center">

        {/* Badge */}
        <div className="flex justify-center mb-6">
          <span
            className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border"
            style={{ color: '#F7931A', borderColor: 'rgba(247,147,26,0.3)', background: 'rgba(247,147,26,0.08)' }}
          >
            <Activity size={11} />
            IN DEVELOPMENT
          </span>
        </div>

        {/* Heading */}
        <div className="text-center mb-4">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-gradient">
            On-Chain Intelligence
          </h1>
          <p className="text-gray-400 text-base leading-relaxed max-w-lg mx-auto">
            A live Bitcoin on-chain dashboard powered by Glassnode, CoinMetrics, and the UTXOMacro data pipeline.
            Built for serious analysts — not noise traders.
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(247,147,26,0.2))' }} />
          <span className="text-gray-700 text-xs uppercase tracking-widest">What's Coming</span>
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(to left, transparent, rgba(247,147,26,0.2))' }} />
        </div>

        {/* Metric grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
          {METRICS.map((m, i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-xl border border-white/5 p-4"
              style={{ background: '#111' }}
            >
              <div
                className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
                style={{ background: 'rgba(247,147,26,0.1)', color: '#F7931A' }}
              >
                {m.icon}
              </div>
              <div>
                <p className="text-white text-sm font-semibold mb-0.5">{m.label}</p>
                <p className="text-gray-600 text-xs leading-relaxed">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Back button */}
        <div className="flex justify-center">
          <button
            onClick={() => onNavigate('home')}
            className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-lg transition-colors"
            style={{ background: '#F7931A', color: '#000' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#e8871a')}
            onMouseLeave={e => (e.currentTarget.style.background = '#F7931A')}
          >
            <ArrowLeft size={15} />
            Back to Home
          </button>
        </div>

      </div>
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default OnChain;
